/**
 * Walking the decision tree: which question comes next, and which answers no longer count.
 *
 * ## Why the walk restarts from the entry every time
 *
 * It would be cheaper to keep a cursor and step it forward. But an answer to an EARLIER
 * question can change the path after it, and a cursor cannot notice: it only knows where it
 * is, not how it got there. Replaying from the entry on every change makes the path a pure
 * function of the answers, which is what makes "go back and change question 2" correct rather
 * than merely possible. The tree is 30 nodes; the cost is nothing.
 *
 * ## Why `orphanedAnswers` exists and must be acted on
 *
 * Change an answer and the branch below it may be replaced wholesale. The answers collected
 * down the old branch are still in the map, and they are not merely stale — they are read by
 * eligibility rules, so a program can be granted on the strength of a question the BD is no
 * longer being asked. The old payload format had no concept of this at all. The flow reports
 * them; the caller drops them.
 *
 * ## Confirmed path vs preview
 *
 * The design shows upcoming questions as collapsed rows, which a decision tree cannot
 * actually know: question 4 depends on the answer to question 3. So the walk produces two
 * things — the path it can prove, and a PREVIEW produced by taking the first branch at each
 * step. The preview is marked, because presenting a guess as a plan is how a progress
 * indicator ends up jumping backwards.
 */
import type { DecisionTreeDocument, DocQuestion, DocTransition } from '../utils/wire';
import { type Answers, isAnswered } from './answerTypes';
import { type EvalContext, evaluateCondition, outTransitions, questionIndex } from './evaluate';

export interface FlowStep {
  question: DocQuestion;
  /** The transition that led here. Absent for the entry question. */
  viaTransitionId?: string;
}

export type FlowBlock =
  /** A condition reads a question that is not answered on this path. */
  | { reason: 'undecidable'; transitionId: string; questionId: string }
  /** Every out-transition evaluated false: the author left a hole. */
  | { reason: 'no-branch'; questionId: string }
  /** The transition points at a question the document does not contain. */
  | { reason: 'dangling'; transitionId: string; toQuestionId: string }
  /** The walk came back to a question it had already visited. */
  | { reason: 'cycle'; questionId: string };

export interface Flow {
  /** Answered questions, in the order the walk reached them. */
  answered: FlowStep[];
  /** The question to answer now. Absent when the walk finished or is blocked. */
  current?: FlowStep;
  /** A guess at what follows, by taking the first branch at each step. See above. */
  preview: FlowStep[];
  /** The walk reached a question with no way out. */
  done: boolean;
  /**
   * The walk could not continue for a reason that is a DEFECT in the tree, not a missing
   * answer. Surfaced rather than swallowed: the BD cannot fix it, but silently showing a
   * finished questionnaire would be worse than saying it is stuck.
   */
  blocked?: FlowBlock;
  /** Answers for questions no longer on the path. The caller must drop these. */
  orphanedAnswers: string[];
}

/** How many questions to guess ahead. Enough to fill the design's list, not so many that the
 *  preview is mostly fiction. */
const PREVIEW_DEPTH = 4;

/**
 * Pick the transition to follow, or say why none can be.
 *
 * First-match-wins over `priority`, which is why `outTransitions` sorts by it rather than
 * trusting document order. An `unknown` stops the walk instead of being skipped: skipping it
 * is exactly the old engine's bug, where a rule over an unanswered question quietly evaluated
 * false and its branch was never taken.
 */
function chooseTransition(
  doc: DecisionTreeDocument,
  questionId: string,
  answers: Answers,
  ctx: EvalContext,
): { taken: DocTransition } | { blocked: FlowBlock } | { done: true } {
  const outs = outTransitions(doc, questionId);
  if (outs.length === 0) return { done: true };

  for (const t of outs) {
    const r = evaluateCondition(t.condition, answers, ctx);
    if (r === true) return { taken: t };
    if (r === 'unknown') {
      return {
        blocked: { reason: 'undecidable', transitionId: t.transitionId, questionId },
      };
    }
    // false: try the next one, in priority order.
  }
  return { blocked: { reason: 'no-branch', questionId } };
}

export function computeFlow(
  doc: DecisionTreeDocument,
  answers: Answers,
  ctx: EvalContext = {},
): Flow {
  const byId = questionIndex(doc);
  const answeredSteps: FlowStep[] = [];
  const onPath = new Set<string>();
  let current: FlowStep | undefined;
  let blocked: FlowBlock | undefined;
  let done = false;

  let step: FlowStep | undefined = byId.has(doc.entryQuestionId)
    ? { question: byId.get(doc.entryQuestionId)! }
    : undefined;

  if (!step) {
    blocked = { reason: 'dangling', transitionId: '(entry)', toQuestionId: doc.entryQuestionId };
  }

  while (step) {
    const id = step.question.questionId;
    if (onPath.has(id)) {
      blocked = { reason: 'cycle', questionId: id };
      break;
    }
    onPath.add(id);

    if (!isAnswered(answers[id])) {
      current = step;
      break;
    }
    answeredSteps.push(step);

    const choice = chooseTransition(doc, id, answers, ctx);
    if ('done' in choice) {
      done = true;
      break;
    }
    if ('blocked' in choice) {
      blocked = choice.blocked;
      break;
    }
    const next = byId.get(choice.taken.toQuestionId);
    if (!next) {
      blocked = {
        reason: 'dangling',
        transitionId: choice.taken.transitionId,
        toQuestionId: choice.taken.toQuestionId,
      };
      break;
    }
    step = { question: next, viaTransitionId: choice.taken.transitionId };
  }

  /*
   * The preview. Starts after the current question (or after the last answered one when the
   * walk is blocked), and takes the FIRST transition each time regardless of its condition —
   * which is the only thing possible, since the answers that would decide it do not exist yet.
   */
  const preview: FlowStep[] = [];
  const seen = new Set(onPath);
  let from = current?.question.questionId ?? answeredSteps.at(-1)?.question.questionId;
  while (from && preview.length < PREVIEW_DEPTH && !done && !blocked) {
    const outs = outTransitions(doc, from);
    if (outs.length === 0) break;
    const t = outs[0];
    const next = byId.get(t.toQuestionId);
    if (!next || seen.has(next.questionId)) break;
    seen.add(next.questionId);
    preview.push({ question: next, viaTransitionId: t.transitionId });
    from = next.questionId;
  }

  /*
   * Answers off the path.
   *
   * Everything answered that the walk did not reach. Note this includes answers BELOW the
   * current question — those are the ones a changed answer stranded, and they are the whole
   * reason this is computed rather than assumed empty.
   */
  const orphanedAnswers = Object.keys(answers).filter(
    (id) => isAnswered(answers[id]) && !onPath.has(id),
  );

  return { answered: answeredSteps, current, preview, done, blocked, orphanedAnswers };
}

/** Drop the answers the flow says are no longer on the path. */
export function pruneAnswers(answers: Answers, orphaned: readonly string[]): Answers {
  if (orphaned.length === 0) return answers;
  const next = { ...answers };
  for (const id of orphaned) delete next[id];
  return next;
}
